import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { addDoc, collection, deleteDoc, doc, getDoc, getDocs, getFirestore, query, Timestamp, updateDoc, where, writeBatch } from "firebase/firestore";
import dayjs from "dayjs";

import { Header } from "../../components/Header/Header";
import { useUser } from "../../context/UserContext";
import { Employee } from "../../types/employee";
import { PtoRequest } from "../../types/adjustments";

const db = getFirestore();

interface PtoRequestWithId extends PtoRequest {
    id: string;
}

export const EmployeePto: React.FC = () => {
    const { employeeId } = useParams();
    const navigate = useNavigate();
    const { userData } = useUser();
    const [employee, setEmployee] = useState<Employee | null>(null);
    const [requests, setRequests] = useState<PtoRequestWithId[]>([]);
    const [loading, setLoading] = useState(true);
    const [manualPtoOpen, setManualPtoOpen] = useState(false);
    const [manualPtoDate, setManualPtoDate] = useState("");
    const [manualPtoHours, setManualPtoHours] = useState("");
    const [manualPtoNotes, setManualPtoNotes] = useState("");
    const [savingManualPto, setSavingManualPto] = useState(false);
    const [editingRequestId, setEditingRequestId] = useState<string | null>(null);
    const [editPtoHours, setEditPtoHours] = useState("");
    const [editPtoNotes, setEditPtoNotes] = useState("");
    const [savingEditPto, setSavingEditPto] = useState(false);
    const [deletingRequestId, setDeletingRequestId] = useState<string | null>(null);
    const [message, setMessage] = useState("");

    useEffect(() => {
        if (userData && !userData.admin) {
            navigate("/");
        }
    }, [userData, navigate]);

    const fetchEmployeePto = useCallback(async () => {
        if (!employeeId) {
            setLoading(false);
            return;
        }

        try {
            const employeeSnapshot = await getDoc(doc(db, "employees", employeeId));
            if (employeeSnapshot.exists()) {
                setEmployee({ id: employeeSnapshot.id, ...employeeSnapshot.data() } as Employee);
            }

            const q = query(
                collection(db, "pto_requests"),
                where("userId", "==", employeeId)
            );
            const snapshot = await getDocs(q);
            const results = snapshot.docs.map((docSnapshot) => ({
                id: docSnapshot.id,
                ...docSnapshot.data(),
            })) as PtoRequestWithId[];

            results.sort((a, b) => a.startDate.localeCompare(b.startDate));
            setRequests(results);
        } catch (error) {
            console.error("Error fetching PTO requests:", error);
        } finally {
            setLoading(false);
        }
    }, [employeeId]);

    useEffect(() => {
        fetchEmployeePto();
    }, [fetchEmployeePto]);

    const formatDateString = (input: string) => {
        const [year, month, day] = input.split("-");
        return `${month}-${day}-${year}`;
    };

    const groupedRequests = useMemo(() => {
        const approved: PtoRequestWithId[] = [];
        const declined: PtoRequestWithId[] = [];
        const pending: PtoRequestWithId[] = [];
        const other: PtoRequestWithId[] = [];

        requests.forEach((request) => {
            const status = (request.status || "").toLowerCase();
            if (status === "approved") {
                approved.push(request);
            } else if (status === "declined" || status === "denied") {
                declined.push(request);
            } else if (status === "pending") {
                pending.push(request);
            } else {
                other.push(request);
            }
        });

        return { approved, declined, pending, other };
    }, [requests]);

    const getRequestApprovedHours = useCallback((request: PtoRequestWithId) => {
        if (typeof request.approvedHours === "number") {
            return request.approvedHours;
        }

        return Number(request.approvedDays ?? 0) * (employee?.ptoTakenHoursPerDay ?? 0);
    }, [employee?.ptoTakenHoursPerDay]);

    const calculateApprovedPtoHours = useCallback((requestsToTotal: PtoRequestWithId[]) => {
        return requestsToTotal.reduce((total, request) => {
            if (request.status?.toLowerCase() !== "approved") return total;

            return total + getRequestApprovedHours(request);
        }, 0);
    }, [getRequestApprovedHours]);

    const approvedPtoHours = useMemo(() => {
        return calculateApprovedPtoHours(requests);
    }, [calculateApprovedPtoHours, requests]);

    const fetchRequestsForEmployee = async () => {
        if (!employeeId) return [];

        const requestsQuery = query(
            collection(db, "pto_requests"),
            where("userId", "==", employeeId)
        );
        const requestsSnapshot = await getDocs(requestsQuery);
        return requestsSnapshot.docs.map((docSnapshot) => ({
            id: docSnapshot.id,
            ...docSnapshot.data(),
        })) as PtoRequestWithId[];
    };

    const syncPtoUsedForRequests = async (requestsToSync: PtoRequestWithId[]) => {
        if (requestsToSync.length === 0) return 0;

        const totalPtoUsed = calculateApprovedPtoHours(requestsToSync);
        const batch = writeBatch(db);

        requestsToSync.forEach((request) => {
            batch.update(doc(db, "pto_requests", request.id), { ptoUsed: totalPtoUsed });
        });

        await batch.commit();
        return totalPtoUsed;
    };

    const resetManualPtoForm = () => {
        setManualPtoDate("");
        setManualPtoHours("");
        setManualPtoNotes("");
    };

    const handleSubmitManualPto = async () => {
        if (!userData?.admin || !employeeId || !employee) return;

        const hours = Number(manualPtoHours);
        if (!manualPtoDate) {
            setMessage("Select a sick PTO date.");
            return;
        }

        if (!Number.isFinite(hours) || hours <= 0) {
            setMessage("Enter sick PTO hours greater than zero.");
            return;
        }

        const approvedDays = employee.ptoTakenHoursPerDay > 0
            ? hours / employee.ptoTakenHoursPerDay
            : 0;
        const hoursWorked = employee.accruedMinutesYTD ? employee.accruedMinutesYTD / 60 : 0;
        const ptoAllowed = hoursWorked * employee.ptoRatio;
        const totalPtoUsed = approvedPtoHours + hours;

        try {
            setSavingManualPto(true);
            await addDoc(collection(db, "pto_requests"), {
                userId: employeeId,
                firstName: employee.firstName,
                lastName: employee.lastName,
                startDate: manualPtoDate,
                endDate: manualPtoDate,
                status: "approved",
                requestedAt: Timestamp.now(),
                reviewedAt: Timestamp.now(),
                reviewedBy: userData.id,
                ptoAllowed,
                ptoUsed: totalPtoUsed,
                approvedDays,
                approvedHours: hours,
                requestType: "Sick",
                manuallyAdded: true,
                notes: manualPtoNotes.trim(),
            });

            const updatedRequests = await fetchRequestsForEmployee();
            await syncPtoUsedForRequests(updatedRequests);

            setMessage("Manual sick PTO added.");
            resetManualPtoForm();
            setManualPtoOpen(false);
            await fetchEmployeePto();
        } catch (error) {
            console.error("Error adding manual sick PTO:", error);
            setMessage("Failed to add manual sick PTO.");
        } finally {
            setSavingManualPto(false);
        }
    };

    const startEditingPto = (request: PtoRequestWithId) => {
        setEditingRequestId(request.id);
        setEditPtoHours(getRequestApprovedHours(request).toString());
        setEditPtoNotes(request.notes || "");
        setMessage("");
    };

    const cancelEditingPto = () => {
        setEditingRequestId(null);
        setEditPtoHours("");
        setEditPtoNotes("");
    };

    const handleUpdateApprovedPto = async (request: PtoRequestWithId) => {
        if (!userData?.admin || !employee) return;

        const hours = Number(editPtoHours);
        if (!Number.isFinite(hours) || hours < 0) {
            setMessage("Enter PTO hours of zero or more.");
            return;
        }

        const approvedDays = employee.ptoTakenHoursPerDay > 0
            ? hours / employee.ptoTakenHoursPerDay
            : Number(request.approvedDays ?? 0);

        try {
            setSavingEditPto(true);
            await updateDoc(doc(db, "pto_requests", request.id), {
                approvedHours: hours,
                approvedDays,
                notes: editPtoNotes.trim(),
                reviewedAt: Timestamp.now(),
                reviewedBy: userData.id,
            });

            const updatedRequests = await fetchRequestsForEmployee();
            await syncPtoUsedForRequests(updatedRequests);
            await fetchEmployeePto();

            cancelEditingPto();
            setMessage("Approved PTO updated.");
        } catch (error) {
            console.error("Error updating approved PTO:", error);
            setMessage("Failed to update approved PTO.");
        } finally {
            setSavingEditPto(false);
        }
    };

    const getDatesInRange = (startDate: string, endDate: string) => {
        const start = dayjs(startDate);
        const end = dayjs(endDate);
        const dates: string[] = [];

        if (!start.isValid() || !end.isValid() || end.isBefore(start, "day")) {
            return dates;
        }

        for (let currentDate = start; !currentDate.isAfter(end, "day"); currentDate = currentDate.add(1, "day")) {
            dates.push(currentDate.format("YYYY-MM-DD"));
        }

        return dates;
    };

    const requestCoversDate = (request: PtoRequestWithId, date: string) => {
        return request.status?.toLowerCase() === "approved" &&
            request.startDate <= date &&
            request.endDate >= date;
    };

    const removeUnusedPtoSchedules = async (
        deletedRequest: PtoRequestWithId,
        remainingRequests: PtoRequestWithId[]
    ) => {
        const dates = getDatesInRange(deletedRequest.startDate, deletedRequest.endDate);

        await Promise.all(dates.map(async (date) => {
            const stillCoveredByPto = remainingRequests.some((request) => requestCoversDate(request, date));
            if (stillCoveredByPto) return;

            const scheduleRef = doc(db, "schedules", `${deletedRequest.userId}_${date}`);
            const scheduleSnapshot = await getDoc(scheduleRef);
            if (scheduleSnapshot.exists() && scheduleSnapshot.data().location === "Pto") {
                await deleteDoc(scheduleRef);
            }
        }));
    };

    const handleDeleteApprovedPto = async (request: PtoRequestWithId) => {
        if (!userData?.admin) return;

        const confirmed = window.confirm("Delete this approved PTO request entirely?");
        if (!confirmed) return;

        try {
            setDeletingRequestId(request.id);
            await deleteDoc(doc(db, "pto_requests", request.id));

            const remainingRequests = await fetchRequestsForEmployee();
            await syncPtoUsedForRequests(remainingRequests);
            if (!request.manuallyAdded) {
                await removeUnusedPtoSchedules(request, remainingRequests);
            }
            await fetchEmployeePto();

            if (editingRequestId === request.id) {
                cancelEditingPto();
            }
            setMessage("Approved PTO deleted.");
        } catch (error) {
            console.error("Error deleting approved PTO:", error);
            setMessage("Failed to delete approved PTO.");
        } finally {
            setDeletingRequestId(null);
        }
    };

    const renderRequests = (title: string, items: PtoRequestWithId[], emptyLabel: string) => {
        return (
            <section className="pto-requests">
                <div className="pto-section-header">
                    <h3>{title}</h3>
                </div>
                {items.length === 0 ? (
                    <p className="pto-empty">{emptyLabel}</p>
                ) : (
                    <div>
                        {items.map((request) => (
                            <div key={request.id} className="request-block">
                                <div className="request-data">
                                    <div className="pto-request-card">
                                        <div className="pto-request-row pto-request-dates">
                                            <span className="pto-request-label">Dates</span>
                                            <span className="pto-request-value">
                                                {formatDateString(request.startDate)} to {formatDateString(request.endDate)}
                                            </span>
                                        </div>
                                        <div className="pto-request-row pto-request-status">
                                            <span className="pto-request-label">Status</span>
                                            <span className="pto-request-value">{request.status}</span>
                                        </div>
                                        {request.manuallyAdded || request.requestType ? (
                                            <div className="pto-request-row">
                                                <span className="pto-request-label">Type</span>
                                                <span className="pto-request-value">
                                                    {request.manuallyAdded ? "Manual " : ""}{request.requestType || "PTO"}
                                                </span>
                                            </div>
                                        ) : null}
                                        {request.status?.toLowerCase() === "approved" ? (
                                            <div className="pto-request-row pto-request-approved">
                                                <span className="pto-request-label">PTO Used</span>
                                                <span className="pto-request-value">
                                                    {typeof request.approvedHours === "number"
                                                        ? `${request.approvedHours} hours`
                                                        : `${request.approvedDays} days`}
                                                </span>
                                            </div>
                                        ) : null}
                                        {request.notes ? (
                                            <div className="pto-request-row">
                                                <span className="pto-request-label">Notes</span>
                                                <span className="pto-request-value">{request.notes}</span>
                                            </div>
                                        ) : null}
                                        {request.status?.toLowerCase() === "approved" ? (
                                            editingRequestId === request.id ? (
                                                <div className="edit-pto-form">
                                                    <label>
                                                        PTO hours used
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            step="0.25"
                                                            value={editPtoHours}
                                                            onChange={(event) => setEditPtoHours(event.target.value)}
                                                        />
                                                    </label>
                                                    <label>
                                                        Notes
                                                        <textarea
                                                            value={editPtoNotes}
                                                            onChange={(event) => setEditPtoNotes(event.target.value)}
                                                            placeholder="Optional"
                                                        />
                                                    </label>
                                                    <div className="pto-request-actions">
                                                        <button
                                                            className="pto-action-button"
                                                            onClick={() => handleUpdateApprovedPto(request)}
                                                            disabled={savingEditPto}
                                                        >
                                                            {savingEditPto ? "Saving..." : "Save"}
                                                        </button>
                                                        <button className="pto-action-button secondary" onClick={cancelEditingPto}>
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="pto-request-actions">
                                                    <button className="pto-action-button" onClick={() => startEditingPto(request)}>
                                                        Edit
                                                    </button>
                                                    <button
                                                        className="pto-action-button danger"
                                                        onClick={() => handleDeleteApprovedPto(request)}
                                                        disabled={deletingRequestId === request.id}
                                                    >
                                                        {deletingRequestId === request.id ? "Deleting..." : "Delete"}
                                                    </button>
                                                </div>
                                            )
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>
        );
    };

    return (
        <div className="employee-pto-container">
            <Header />
            <main className="employee-pto-content">
                <div className="employee-pto-header">
                    <h2>
                        PTO Requests
                        {employee ? ` - ${employee.firstName} ${employee.lastName}` : ""}
                    </h2>
                    <div className="employee-pto-actions">
                        <button className="employee-pto-add" onClick={() => setManualPtoOpen((open) => !open)}>
                            {manualPtoOpen ? "Cancel Manual PTO" : "Add Sick PTO"}
                        </button>
                        <button className="employee-pto-back" onClick={() => navigate("/payroll")}>
                            Back to Payroll
                        </button>
                    </div>
                </div>
                {message ? <p className="employee-pto-message">{message}</p> : null}
                {manualPtoOpen ? (
                    <section className="manual-pto-panel">
                        <div className="manual-pto-heading">
                            <h3>Add Manual Sick PTO</h3>
                            {employee ? (
                                <span>1 day = {employee.ptoTakenHoursPerDay} hours for this employee</span>
                            ) : null}
                        </div>
                        <div className="manual-pto-form">
                            <label>
                                Sick day date
                                <input
                                    type="date"
                                    value={manualPtoDate}
                                    onChange={(event) => setManualPtoDate(event.target.value)}
                                />
                            </label>
                            <label>
                                PTO hours used
                                <input
                                    type="number"
                                    min="0"
                                    step="0.25"
                                    value={manualPtoHours}
                                    onChange={(event) => setManualPtoHours(event.target.value)}
                                />
                            </label>
                            <label>
                                Notes
                                <textarea
                                    value={manualPtoNotes}
                                    onChange={(event) => setManualPtoNotes(event.target.value)}
                                    placeholder="Optional"
                                />
                            </label>
                        </div>
                        <button
                            className="manual-pto-submit"
                            onClick={handleSubmitManualPto}
                            disabled={savingManualPto}
                        >
                            {savingManualPto ? "Saving..." : "Save Sick PTO"}
                        </button>
                    </section>
                ) : null}
                {loading ? (
                    <p>Loading PTO requests...</p>
                ) : (
                    <div className="employee-pto-grid">
                        {renderRequests("Approved", groupedRequests.approved, "No approved PTO requests.")}
                        {renderRequests("Pending", groupedRequests.pending, "No pending PTO requests.")}
                        {renderRequests("Denied", groupedRequests.declined, "No denied PTO requests.")}
                        {groupedRequests.other.length > 0
                            ? renderRequests("Other", groupedRequests.other, "No other PTO requests.")
                            : null}
                    </div>
                )}
            </main>
        </div>
    );
};

require("./styles.scss");
